import React, { useRef } from 'react'
import { type RootState } from '../../../store'
import { type ConnectedProps, connect, useSelector } from 'react-redux'
import { HeaderSummaryAssetKPIDataSocketUpdateAction } from '../redux/getAssetDashboardHeaderKPISummarySlice'
import { HeaderSummaryAssetRegisterDataSocketUpdateAction } from '../redux/getAssetDashboardHeaderRegisterSummarySlice'
import { type HealthSummaryState } from '../../device-dashboard/redux/getHealthSummarySlice'
import { useWebSocket } from '../../../contexts/WebSocketContext'
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { getHeaderSummaryKPIAssetDashboardService, getHeaderSummaryRegisterAssetDashboardService, saveDefaultViewDataservice } from '../redux/actionCreator'
import { type SaveDefaultViewRequest } from '../services/saveDeafultViewService'

const initialState = {
  deviceName1: 'NA',
  deviceParameterName1: 'NA',
  deviceParameterData1: 'NA',
  deviceParameterColor1: 'NA',
  deviceName2: 'NA',
  deviceParameterName2: 'NA',
  deviceParameterData2: 'NA',
  deviceParameterColor2: 'NA',
  deviceName3: 'NA',
  deviceParameterName3: 'NA',
  deviceParameterData3: 'NA',
  deviceParameterColor3: 'NA',
  deviceName4: 'NA',
  deviceParameterName4: 'NA',
  deviceParameterData4: 'NA',
  deviceParameterColor4: 'NA',
  deviceName5: 'NA',
  deviceParameterName5: 'NA',
  deviceParameterData5: 'NA',
  deviceParameterColor5: 'NA'
}

const AssetHeaderSummary = (props: AllProps): JSX.Element => {
  const titleMaxLen = 15
  const saveDefaultViewObject = useSelector((state: RootState) => state.saveDefaultViewObject.saveDefaultViewObject)
  const { messages, subscribeToAssetModBus, subscribeToAssetKPIs, isConnected } = useWebSocket()
  const lastPayloadRef = useRef<string | null>(null)
  const lastProcessedMessageCountRef = useRef<number>(0) // Track last processed message count instead of IDs
  const healthScoreData = useSelector((state: RootState) => state.HealthScoreAssetDashboard)
  const assetRegisterSummaryDatastatus = useSelector((state: RootState) => state.headerSummaryRegisterAssetData)
  const assetKPISummaryDatastatus = useSelector((state: RootState) => state.headerSummaryKPIAssetData)
  const [addState, dispatch] = React.useReducer(
    (curVal: any, newVal: any) => ({ ...newVal }),
    initialState
  )
  const assetRegisterSummaryData = useSelector((state: RootState) => state.headerSummaryRegisterAssetData.headerSummaryAssetRegisterData
  )
  const assetKPISummaryData = useSelector((state: RootState) => state.headerSummaryKPIAssetData.headerSummaryAssetKPIData)

  const generateDisplayText = (textInput: string, length: number): any => {
    if (textInput !== undefined) {
      const isTextLong = textInput.length > length
      return isTextLong ? `${textInput.substring(0, length)}...` : textInput
    }
  }

  const isToolTipVisible = (textInput: string, length: number): any => {
    if (textInput !== undefined) {
      return textInput.length > length ? textInput : ''
    }
  }

  const retryAssetStatus = (): void => {
    props.getHeaderSummaryRegisterAssetDashboardService(props.parentSiteId, props.parentAssetId, 'assetStatus', null, true)
    props.getHeaderSummaryKPIAssetDashboardService(props.parentSiteId, props.parentAssetId, 'assetStatus', null, true)
  }

  // Subscribe to asset status only once or when parentAssetId changes
  React.useEffect(() => {
    if (isConnected && (saveDefaultViewObject != null)) {
      const defaultWidgetData = saveDefaultViewObject
      // Extend widget type to include optional assets property
      interface AssetStatusWidget {
        widgetId: string
        displayname: string
        devices: any[]
        assets?: any[]
      }
      const assetStatusWidget: AssetStatusWidget | undefined = Array.isArray(defaultWidgetData?.assetDashBoardDefaultView)
        ? defaultWidgetData.assetDashBoardDefaultView.find((widget: any) => widget.widgetId === 'assetStatus')
        : undefined

      if (assetStatusWidget != null) {
        const modBusData = assetStatusWidget?.devices?.filter(d => Array.isArray(d.cimCodeIds) && d.cimCodeIds.length > 0)
          .map(d => ({ deviceId: d.deviceId, cimCodeIds: d.cimCodeIds }))

        const kpiData = assetStatusWidget.devices?.filter(d => Array.isArray(d.kpidefinitionIds) && d.kpidefinitionIds.length > 0)
          .map(d => ({ deviceId: d.deviceId, kpiDefinitionIds: d.kpidefinitionIds })) ?? []

        const assetkpiData = (((assetStatusWidget.assets?.filter((d: any) => Array.isArray(d.kpidefinitionIds) && d.kpidefinitionIds.length > 0)
          .map((d: any) => ({ assetId: d.assetId, kpiDefinitionIds: d.kpidefinitionIds }))))) ?? []

        const assetKPIPayload = { devices: kpiData, assets: assetkpiData }

        const currentPayload = JSON.stringify({ modBusData, assetKPIPayload })

        if (lastPayloadRef.current !== currentPayload) {
          lastPayloadRef.current = currentPayload
          if (modBusData !== undefined && modBusData?.length > 0) {
            subscribeToAssetModBus(modBusData)
              .catch(() => {
                // Handle ModBus subscription error if needed
              })
          }
          if (kpiData.length > 0 || assetkpiData.length > 0) {
            subscribeToAssetKPIs(assetKPIPayload)
              .catch(() => {
                // Handle ModBus subscription error if needed
              })
          }
        }
      }
    }
  }, [isConnected, saveDefaultViewObject, subscribeToAssetModBus, subscribeToAssetKPIs, props.parentAssetId])

  // Display WebSocket messages of type DASHBOARD_MODBUS_DATA_UPDATE
  React.useEffect(() => {
    // istanbul ignore next
    if (Array.isArray(messages)) {
      // Check if the latest message is a HEARTBEAT and return early
      if (messages.length > 0 && messages[messages.length - 1]?.type === 'HEARTBEAT') {
        return
      }

      // Only process new messages since last processing
      const newMessages = messages.slice(lastProcessedMessageCountRef.current)
      lastProcessedMessageCountRef.current = messages.length

      // Handle Modbus messages - only process new messages
      const modbusMessages = newMessages.filter(
        (msg: any) => msg?.type === 'DASHBOARD_MODBUS_DATA_UPDATE'
      )

      if (modbusMessages.length > 0 && (saveDefaultViewObject != null)) {
        modbusMessages.forEach((msg: any) => {
          const wizard = saveDefaultViewObject?.assetDashBoardDefaultView?.filter(a => a.widgetId === 'assetStatus')
          if (
            Array.isArray(wizard?.[0]?.devices) &&
               wizard[0].devices.filter(e => Array.isArray(e.cimCodeIds) && e.cimCodeIds.includes(msg?.data?.registerReadings?.[0]?.cimCodeId)).length > 0
          ) {
            props.headerSummaryAssetRegisterDataSocketUpdateAction(msg)
          }
        })
      }

      // Handle KPI messages - only process new messages
      const kpiMessages = newMessages.filter(
        (msg: any) => msg?.type === 'DASHBOARD_KPI_DATA_UPDATE'
      )

      if (kpiMessages.length > 0 && (saveDefaultViewObject != null)) {
        kpiMessages.forEach((msg: any) => {
          const wizard = saveDefaultViewObject?.assetDashBoardDefaultView?.filter(a => a.widgetId === 'assetStatus')
          const widget = wizard?.[0]
          const assetsArray: Array<{ assetId?: string, kpidefinitionIds?: string[] }> =
               widget != null && Array.isArray((widget as { assets?: Array<{ assetId?: string, kpidefinitionIds?: string[] }> }).assets)
                 ? (widget as { assets?: Array<{ assetId?: string, kpidefinitionIds?: string[] }> }).assets ?? []
                 : []
          const deviceArray: Array<{ deviceId?: string, kpidefinitionIds?: string[] }> =
               widget != null && Array.isArray((widget as { devices?: Array<{ assetId?: string, kpidefinitionIds?: string[], cimCodeIds?: string[] }> }).devices)
                 ? (widget as { devices?: Array<{ deviceId?: string, kpidefinitionIds?: string[] }> }).devices ?? []
                 : []
          if ([...assetsArray, ...deviceArray].filter(e => e.kpidefinitionIds?.includes(msg?.data?.kpi_data?.[0]?.kpi_definition_id)).length > 0) {
            props.headerSummaryAssetKPIDataSocketUpdateAction(msg)
          }
        })
      }
    }
  }, [messages])

  React.useEffect(() => {
    // Compose the summary state from assetRegisterSummaryData and assetKPISummaryData
    if (assetRegisterSummaryData != null || assetKPISummaryData != null) {
      const transformedData: Array<Record<string, string>> = []
      let parameterCounter = 1

      // Asset-level KPIs (from assetKPISummaryData.assetKPIs)
      if ((assetKPISummaryData != null) && Array.isArray(assetKPISummaryData.assetKPIs)) {
        assetKPISummaryData.assetKPIs.forEach((kpi: any) => {
          transformedData.push({
            [`deviceName${parameterCounter}`]: assetKPISummaryData.asset_name,
            [`deviceParameterName${parameterCounter}`]: kpi.kpi_name,
            [`deviceParameterData${parameterCounter}`]: kpi.formattedValue !== undefined && kpi.formattedValue !== null
              ? kpi.formattedValue
              : (kpi.kpi_value !== null ? kpi.kpi_value.toString() : null),
            [`deviceParameterColor${parameterCounter}`]: kpi.colorValue,
            [`deviceParameterUnits${parameterCounter}`]: kpi.formattedValue !== undefined && kpi.formattedValue !== null
              ? ''
              : kpi.units,
            [`deviceParameterExecutionStatus${parameterCounter}`]: kpi.execution_status
          })
          parameterCounter++
        })
      }

      if ((assetKPISummaryData != null) && Array.isArray(assetKPISummaryData.devices)) {
        assetKPISummaryData.devices.forEach((device: any) => {
          const deviceName = device.device_name
          if (Array.isArray(device.kpiData)) {
            device.kpiData.forEach((kpi: any) => {
              transformedData.push({
                [`deviceName${parameterCounter}`]: deviceName,
                [`deviceParameterName${parameterCounter}`]: kpi.kpi_name,
                [`deviceParameterData${parameterCounter}`]: kpi.formattedValue !== undefined && kpi.formattedValue !== null
                  ? kpi.formattedValue
                  : (kpi.kpi_value !== null ? kpi.kpi_value.toString() : null),
                [`deviceParameterColor${parameterCounter}`]: kpi.colorValue,
                [`deviceParameterUnits${parameterCounter}`]: kpi.formattedValue !== undefined && kpi.formattedValue !== null
                  ? ''
                  : kpi.units,
                [`deviceParameterExecutionStatus${parameterCounter}`]: kpi.execution_status
              })
              parameterCounter++
            })
          }
        })
      }

      // Device-level Registers (from assetRegisterSummaryData.devices[].register_readings)
      if ((assetRegisterSummaryData != null) && Array.isArray(assetRegisterSummaryData.devices)) {
        assetRegisterSummaryData.devices.forEach((device: any) => {
          const deviceName = device.device_name
          if (Array.isArray(device.registerReadings)) {
            device.registerReadings.forEach((reading: any) => {
              transformedData.push({
                [`deviceName${parameterCounter}`]: deviceName,
                [`deviceParameterName${parameterCounter}`]: reading.registerName,
                [`deviceParameterData${parameterCounter}`]: reading.formattedValue !== undefined && reading.formattedValue !== null
                  ? reading.formattedValue
                  : (reading.indexValue !== null ? reading.indexValue.toString() : null),
                [`deviceParameterColor${parameterCounter}`]: reading.colorValue,
                [`deviceParameterUnits${parameterCounter}`]: reading.formattedValue !== undefined && reading.formattedValue !== null
                  ? ''
                  : reading.units,
                [`deviceParameterExecutionStatus${parameterCounter}`]: reading.execution_status
              })
              parameterCounter++
            })
          }
        })
      }

      const updatedState = transformedData.reduce((acc, cur) => ({ ...acc, ...cur }), {})
      dispatch(updatedState)
    }
    return () => { dispatch(initialState) }
  }, [assetRegisterSummaryData, assetKPISummaryData])

  React.useEffect(() => {
    // istanbul ignore next
    if (props.saveDefaultView) {
      // Build devices array from assetRegisterSummaryData and assetKPISummaryData
      const devicesMap: Record<string, { deviceId: string, cimCodeIds: string[], kpidefinitionIds: string[] }> = {}

      // From assetRegisterSummaryData: device-level registers
      // istanbul ignore next
      if ((assetRegisterSummaryData != null) && Array.isArray(assetRegisterSummaryData.devices)) {
        assetRegisterSummaryData.devices.forEach((device: any) => {
          if (devicesMap[device.device_id] === undefined) {
            devicesMap[device.device_id] = {
              deviceId: device.device_id,
              cimCodeIds: [],
              kpidefinitionIds: []
            }
          }
          if (Array.isArray(device.registerReadings)) {
            devicesMap[device.device_id].cimCodeIds = device.registerReadings.map((reading: any) => reading.cimCodeId)
          }
        })
      }

      // From assetKPISummaryData: device-level KPIs
      // istanbul ignore next
      if ((assetKPISummaryData != null) && Array.isArray(assetKPISummaryData.devices)) {
        assetKPISummaryData.devices.forEach((device: any) => {
          if (devicesMap[device.device_id] === undefined) {
            devicesMap[device.device_id] = {
              deviceId: device.device_id,
              cimCodeIds: [],
              kpidefinitionIds: []
            }
          }
          if (Array.isArray(device.kpiData)) {
            devicesMap[device.device_id].kpidefinitionIds = device.kpiData.map((kpi: any) => kpi.kpi_definition_id)
          }
        })
      }

      // Asset-level KPIs (not attached to a device)
      let assetLevelKpiDefinitionIds: string[] = []
      if ((assetKPISummaryData != null) && Array.isArray(assetKPISummaryData.assetKPIs)) {
        assetLevelKpiDefinitionIds = assetKPISummaryData.assetKPIs.map((kpi: any) => kpi.kpi_definition_id)
      }

      const devices = Object.values(devicesMap)

      const assetDashBoardDefaultView = {
        assetDashBoardDefaultView: [
          {
            widgetId: 'assetStatus',
            displayname: 'Asset Status',
            devices,
            assets: assetLevelKpiDefinitionIds.length > 0
              ? [
                  {
                    assetId: ((assetKPISummaryData != null) && typeof assetKPISummaryData.asset_id === 'string' && assetKPISummaryData.asset_id !== '' ? assetKPISummaryData.asset_id : props.parentAssetId),
                    kpidefinitionIds: assetLevelKpiDefinitionIds
                  }
                ]
              : []
          }
        ]
      }

      props.saveDefaultViewDataservice(props.parentAssetId, assetDashBoardDefaultView)
    }
  }, [props.saveDefaultView, assetRegisterSummaryData, assetKPISummaryData, props.parentAssetId])

  // Separate useEffect for health summary data changes
  React.useEffect(() => {
    if (props.healthSummaryData?.data?.data != null) {
      // Call register service if register data is available and devices array is not empty
      if (assetRegisterSummaryDatastatus?.headerSummaryAssetRegisterData?.devices !== null &&
          assetRegisterSummaryDatastatus?.headerSummaryAssetRegisterData?.devices !== undefined &&
          Array.isArray(assetRegisterSummaryDatastatus?.headerSummaryAssetRegisterData?.devices) &&
          assetRegisterSummaryDatastatus.headerSummaryAssetRegisterData.devices.length > 0) {
        props.getHeaderSummaryRegisterAssetDashboardService(props.parentSiteId, props.parentAssetId, 'assetStatus', null, true)
      }

      // Call KPI service if KPI data is available and either assetKPIs or devices array is not empty
      if (assetKPISummaryDatastatus?.headerSummaryAssetKPIData !== null &&
          assetKPISummaryDatastatus?.headerSummaryAssetKPIData !== undefined &&
          ((Array.isArray(assetKPISummaryDatastatus?.headerSummaryAssetKPIData?.assetKPIs) &&
            assetKPISummaryDatastatus.headerSummaryAssetKPIData.assetKPIs.length > 0) ||
           (Array.isArray(assetKPISummaryDatastatus?.headerSummaryAssetKPIData?.devices) &&
            assetKPISummaryDatastatus.headerSummaryAssetKPIData.devices.length > 0))) {
        props.getHeaderSummaryKPIAssetDashboardService(props.parentSiteId, props.parentAssetId, 'assetStatus', null, true)
      }
    }
  }, [props.healthSummaryData])

  return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#272727',
          padding: '10px 20px',
          borderRadius: '8px',
          color: '#f0f0f0',
          height: '90px',
          marginTop: '20px'
        }}
      >
        <Box>
          <Typography style={{ fontSize: '16px', color: '#f0f0f0' }} >{props.parentAssetName}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Typography style={{ fontSize: '20px', color: '#f0f0f0' }} >Health Score</Typography>
            <Typography
              sx={{ color: healthScoreData.data > 80 ? 'green' : healthScoreData.data > 60 ? 'orange' : 'red', fontWeight: 'bold', fontSize: '20px', marginLeft: '7px' }}
            >
              {typeof healthScoreData.data === 'number' ? `${healthScoreData.data}%` : '-'}
            </Typography>
          </Box>
        </Box>
        {(assetRegisterSummaryDatastatus.httpStatus === 500 || assetRegisterSummaryDatastatus.httpStatus === 404 || assetRegisterSummaryDatastatus.httpStatus === undefined ||
          assetKPISummaryDatastatus.httpStatus === 500 || assetKPISummaryDatastatus.httpStatus === 404 || assetKPISummaryDatastatus.httpStatus === undefined)
          ? ''
          : <Box sx={{ height: '30px', width: '1px', backgroundColor: 'white', margin: '0 10px' }} />
        }
        {(Boolean(assetRegisterSummaryDatastatus.isLoading) || Boolean(assetKPISummaryDatastatus.isLoading))
          ? (
            <div style={{ display: 'flex', height: '350px', justifyContent: 'center', alignItems: 'center' }} >
              <CircularProgress />
            </div>
            )
          : (assetRegisterSummaryDatastatus.httpStatus === 0 || assetRegisterSummaryDatastatus.httpStatus === undefined ||
            assetKPISummaryDatastatus.httpStatus === 0 || assetKPISummaryDatastatus.httpStatus === undefined)
              ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography style={{ fontSize: '16px', color: '#f0f0f0' }}>Network Error !! <span style={{ color: '#64c3ff', cursor: 'pointer', fontWeight: 'bold' }} onClick={retryAssetStatus}>Click here to retry</span></Typography>
              </Box>
                )
              : ((assetRegisterSummaryDatastatus.httpStatus === 404 && assetKPISummaryDatastatus.httpStatus === 404) ||
              (assetRegisterSummaryDatastatus.httpStatus === 204 && assetKPISummaryDatastatus.httpStatus === 204))
                  ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography style={{ fontSize: '16px', color: '#f0f0f0' }}>No Parameter Or KPI Is Selected. Please Choose One To Display</Typography>
              </Box>
                    )
                  : (
                      Array.from({ length: 5 }, (_, index) => index + 1).map((i) => (
                        addState[`deviceName${i}`] !== 'NA' && (
                  <React.Fragment key={i}>
                    <Box>
                      <Tooltip title={isToolTipVisible(addState[`deviceName${i}`], titleMaxLen)}>
                        <Typography style={{ fontSize: '16px', color: '#f0f0f0' }}>{generateDisplayText(addState[`deviceName${i}`], titleMaxLen)}</Typography>
                      </Tooltip>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Tooltip title={isToolTipVisible(addState[`deviceParameterName${i}`], titleMaxLen)}>
                          <Typography style={{ fontSize: '20px', color: '#f0f0f0' }}>{generateDisplayText(addState[`deviceParameterName${i}`], titleMaxLen)} </Typography>
                        </Tooltip>
                        <Typography sx={{ color: addState[`deviceParameterColor${i}`], fontWeight: 'bold', fontSize: '16px', marginLeft: '7px' }}>
                          {addState[`deviceParameterExecutionStatus${i}`]?.toLowerCase() === 'failure'
                            ? 'NaN'
                            : addState[`deviceParameterData${i}`] === null
                              ? 'NaN'
                              : (addState[`deviceParameterUnits${i}`]?.toLowerCase() === 'boolean'
                                  ? (Number(addState[`deviceParameterData${i}`]) === 0
                                      ? `Normal (${Number(addState[`deviceParameterData${i}`])})`
                                      : (Number(addState[`deviceParameterData${i}`]) === 1
                                          ? `Fail (${Number(addState[`deviceParameterData${i}`])})`
                                          : addState[`deviceParameterData${i}`]))
                                  : (<>{addState[`deviceParameterData${i}`]}
                                  <span style={{ fontSize: '14px' }}> {addState[`deviceParameterUnits${i}`]}</span>
                                </>))
                          }
                        </Typography>
                      </Box>
                    </Box>
                    {i < 5 && addState[`deviceName${i + 1}`] !== 'NA' && (
                      <Box sx={{ height: '30px', width: '1px', backgroundColor: 'white' }} />
                    )}
                  </React.Fragment>
                        )
                      ))
                    )}
        <Box>
          <div>
            <div>
              <IconButton onClick={props.setIsModalOpen}>
                <MoreVertIcon />
              </IconButton>
              {props.dialogParameterSelection(props.isModalOpen, 'assetStatus')}
            </div>
          </div>
        </Box>
      </Box>
  )
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface StateToProps {
  healthSummaryData: HealthSummaryState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  healthSummaryData: state.HealthSummaryState
})

interface DispatchToProps {
  saveDefaultViewDataservice: (parentAssetId: string, requestParameter: SaveDefaultViewRequest) => void
  getHeaderSummaryRegisterAssetDashboardService: (siteId: string, assetId: string, graphName: string, requestParams: any, isdefault: boolean) => void
  getHeaderSummaryKPIAssetDashboardService: (siteId: string, assetId: string, graphName: string, requestParams: any, isdefault: boolean) => void
  headerSummaryAssetRegisterDataSocketUpdateAction: (payload: any) => void
  headerSummaryAssetKPIDataSocketUpdateAction: (payload: any) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  saveDefaultViewDataservice: (parentAssetId: string, requestParameter: SaveDefaultViewRequest) => dispatch(saveDefaultViewDataservice(parentAssetId, requestParameter)),
  getHeaderSummaryRegisterAssetDashboardService: (siteId: string, assetId: string, graphName: string, requestParams: any, isdefault: boolean) => dispatch(getHeaderSummaryRegisterAssetDashboardService(siteId, assetId, graphName, requestParams, isdefault)),
  getHeaderSummaryKPIAssetDashboardService: (siteId: string, assetId: string, graphName: string, requestParams: any, isdefault: boolean) => dispatch(getHeaderSummaryKPIAssetDashboardService(siteId, assetId, graphName, requestParams, isdefault)),
  headerSummaryAssetRegisterDataSocketUpdateAction: (payload: any) => dispatch(HeaderSummaryAssetRegisterDataSocketUpdateAction(payload)),
  headerSummaryAssetKPIDataSocketUpdateAction: (payload: any) => dispatch(HeaderSummaryAssetKPIDataSocketUpdateAction(payload))
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

interface InputProps {
  parentSiteId: string
  parentAssetId: string
  parentAssetName: string
  saveDefaultView: boolean
  isModalOpen: boolean
  dialogParameterSelection: any
  setIsModalOpen: any
}

type AllProps = PropsFromRedux & InputProps

export const MemoAssetHeaderSummary = React.memo(connector(AssetHeaderSummary))
